import { createReducer, on } from '@ngrx/store';
import { MarketResearchState, initialState } from './market-research.state';
import { BatchProgress, BatchSummary, MatchQueueItem, MatchSuggestion } from '../../models/market-research.model';
import * as MarketResearchActions from './market-research.actions';

/**
 * Confirming/rejecting a row updates its status in place — it never
 * disappears from the table. The user can always change their mind and
 * re-confirm or re-reject, since the row is still right there to click.
 */
function patchQueueItem(
  items: MatchQueueItem[],
  queueId: number,
  patch: Partial<MatchQueueItem>
): MatchQueueItem[] {
  return items.map((item) => (item.queueId === queueId ? { ...item, ...patch } : item));
}

/**
 * A manually-matched org (from "search the full database") was never one of
 * this row's pre-computed suggestions, so topSuggestion() — which only looks
 * inside row.suggestions — has nothing to find by matchedId. Append a
 * synthesized suggestion so the confirmed org still renders as the top match.
 */
function addManualSuggestion(
  items: MatchQueueItem[],
  queueId: number,
  bhiveId: string,
  manualMatch: { companyName: string; companyAddress?: string; bhiveOrg?: boolean }
): MatchQueueItem[] {
  return items.map((item) => {
    if (item.queueId !== queueId) {
      return item;
    }
    if (item.suggestions.some((s) => s.bhiveId === bhiveId)) {
      return item;
    }
    const synthetic: MatchSuggestion = {
      id: -1,
      queueId,
      suggestionRank: 0,
      organizationId: 0,
      companyName: manualMatch.companyName,
      bhiveId,
      companyAddress: manualMatch.companyAddress ?? null,
      // Not algorithmically scored — sourceDb is what the UI keys off to avoid
      // rendering this as if it were a real confidence percentage.
      score: 0,
      sourceDb: 'MANUAL_SEARCH',
      bhiveOrg: manualMatch.bhiveOrg,
      isSelected: true,
    };
    return { ...item, suggestions: [...item.suggestions, synthetic] };
  });
}

/**
 * BatchSummary.needsReview/batchStatus in the resume list is a one-time
 * snapshot (see checkResumeProgress in actions) — it never updates on its
 * own. Whenever we fetch a live BatchProgress for a batch that's already in
 * the list, fold the fresh counts back in so the list stops lying.
 */
function patchBatchSummary(batches: BatchSummary[], progress: BatchProgress): BatchSummary[] {
  return batches.map((batch) =>
    batch.batchId === progress.batchId
      ? {
          ...batch,
          batchStatus: progress.batchStatus ?? batch.batchStatus,
          needsReview: progress.needsReview ?? batch.needsReview,
          autoMatched: progress.autoMatched ?? batch.autoMatched,
          reviewedTotalMatched: progress.reviewedTotalMatched ?? batch.reviewedTotalMatched,
          failed: progress.failed ?? batch.failed,
        }
      : batch
  );
}

export const marketResearchReducer = createReducer(
  initialState,

  on(MarketResearchActions.setMode, (state, { mode }) => ({
    ...state,
    mode,
    reviewTab: 'needs' as const,
  })),

  on(MarketResearchActions.goToStep, (state, { step }) => ({
    ...state,
    currentStep: step,
  })),

  on(MarketResearchActions.resetFlow, () => initialState),

  on(MarketResearchActions.selectBrand, (state, { brand }) => ({
    ...state,
    selectedBrand: brand,
    brandOptions: [],
  })),

  on(MarketResearchActions.searchBrandOrgs, (state) => ({
    ...state,
  })),

  on(MarketResearchActions.searchBrandOrgsSuccess, (state, { brands }) => ({
    ...state,
    brandOptions: brands,
  })),

  on(MarketResearchActions.searchBrandOrgsFail, (state, { error }) => ({
    ...state,
    brandOptions: [],
    error,
  })),

  on(MarketResearchActions.searchResumeBrandOrgs, (state) => ({
    ...state,
  })),

  on(MarketResearchActions.searchResumeBrandOrgsSuccess, (state, { brands }) => ({
    ...state,
    resumeBrandOptions: brands,
  })),

  on(MarketResearchActions.searchResumeBrandOrgsFail, (state, { error }) => ({
    ...state,
    resumeBrandOptions: [],
    error,
  })),

  on(MarketResearchActions.selectFile, (state, { file }) => ({
    ...state,
    uploadFile: file,
  })),

  on(MarketResearchActions.downloadTemplate, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.downloadTemplateSuccess, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.downloadTemplateFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.uploadFile, (state) => ({
    ...state,
    isLoading: true,
    uploadProgress: 'uploading' as const,
    batchProgress: null,
    importCompleted: false,
    importMode: null,
    importResult: null,
    error: null,
  })),

  on(MarketResearchActions.uploadFileSuccess, (state, { batchId }) => ({
    ...state,
    currentBatchId: batchId,
    uploadProgress: 'processing' as const,
    isLoading: true,
    error: null,
  })),

  on(MarketResearchActions.uploadFileFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    uploadProgress: 'idle' as const,
    error,
  })),

  on(MarketResearchActions.startPolling, (state) => ({
    ...state,
    isMatching: true,
  })),

  on(MarketResearchActions.pollProgress, (state) => ({
    ...state,
    isMatching: true,
  })),

  on(MarketResearchActions.pollProgressTick, (state, { progress }) => ({
    ...state,
    batchProgress: progress,
    batches: patchBatchSummary(state.batches, progress),
  })),

  on(MarketResearchActions.pollProgressComplete, (state) => ({
    ...state,
    isMatching: false,
    uploadProgress: 'done' as const,
    isLoading: false,
  })),

  on(MarketResearchActions.pollProgressFail, (state, { error }) => ({
    ...state,
    isMatching: false,
    error,
  })),

  on(MarketResearchActions.loadReviewItems, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.loadReviewItemsSuccess, (state, { response }) => ({
    ...state,
    reviewItems: response.items ?? [],
    totalNeedsReview: response.totalNeedsReview ?? response.items?.length ?? 0,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.loadReviewItemsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.loadAutoMatchedItems, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.loadAutoMatchedItemsSuccess, (state, { items }) => ({
    ...state,
    autoMatched: items,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.loadAutoMatchedItemsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.loadNoMatchItems, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.loadNoMatchItemsSuccess, (state, { items }) => ({
    ...state,
    noMatchItems: items,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.loadNoMatchItemsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.loadAllQueueItems, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.loadAllQueueItemsSuccess, (state, { items }) => ({
    ...state,
    allQueueItems: items,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.loadAllQueueItemsFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.confirmAutoMatchedAbove, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.confirmAutoMatchedAboveSuccess, (state) => ({
    ...state,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.confirmAutoMatchedAboveFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.searchBatches, (state) => ({
    ...state,
    isLoading: true,
  })),

  on(MarketResearchActions.searchBatchesSuccess, (state, { batches }) => ({
    ...state,
    batches,
    isLoading: false,
    error: null,
  })),

  on(MarketResearchActions.searchBatchesFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.selectBatch, (state, { batch }) => ({
    ...state,
    currentBatchId: batch.batchId,
    importCompleted: false,
    importMode: null,
    importResult: null,
  })),

  on(MarketResearchActions.changeReviewTab, (state, { tab }) => ({
    ...state,
    reviewTab: tab,
  })),

  on(MarketResearchActions.confirmMatch, (state) => ({
    ...state,
    isLoading: true,
  })),

  // noMatchItems is patched alongside the rest: a NO_MATCH / AUTO_REJECTED row can
  // still be confirmed (the reviewer decides the weak candidate was right after all, or finds
  // one via the manual search). Leaving it out meant the row kept its old status in the tab it
  // was rendered from, so confirming it looked like it did nothing.
  on(MarketResearchActions.confirmMatchSuccess, (state, { queueId, bhiveId, manualMatch }) => {
    const withSuggestion = manualMatch
      ? {
          reviewItems: addManualSuggestion(state.reviewItems, queueId, bhiveId, manualMatch),
          autoMatched: addManualSuggestion(state.autoMatched, queueId, bhiveId, manualMatch),
          allQueueItems: addManualSuggestion(state.allQueueItems, queueId, bhiveId, manualMatch),
          noMatchItems: addManualSuggestion(state.noMatchItems, queueId, bhiveId, manualMatch),
        }
      : {
          reviewItems: state.reviewItems,
          autoMatched: state.autoMatched,
          allQueueItems: state.allQueueItems,
          noMatchItems: state.noMatchItems,
        };
    return {
      ...state,
      reviewItems: patchQueueItem(withSuggestion.reviewItems, queueId, {
        matchingStatus: 'CONFIRMED',
        matchedId: bhiveId,
      }),
      autoMatched: patchQueueItem(withSuggestion.autoMatched, queueId, {
        matchingStatus: 'CONFIRMED',
        matchedId: bhiveId,
      }),
      allQueueItems: patchQueueItem(withSuggestion.allQueueItems, queueId, {
        matchingStatus: 'CONFIRMED',
        matchedId: bhiveId,
      }),
      noMatchItems: patchQueueItem(withSuggestion.noMatchItems, queueId, {
        matchingStatus: 'CONFIRMED',
        matchedId: bhiveId,
      }),
      isLoading: false,
    };
  }),

  on(MarketResearchActions.confirmMatchFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.rejectMatch, (state) => ({
    ...state,
    isLoading: true,
  })),

  // "Reject" posts bhiveId=0, which the backend persists as NO_MATCH — patch the same
  // status locally so the row lands in the no-match tab and survives a refetch unchanged.
  // matchedId must be reset alongside the status, exactly as the backend does for the "0"
  // sentinel. Patching only the status left a row that had previously been matched still
  // displaying its old BHive id under a "no match" badge — the UI contradicting the DB,
  // which really holds matched_id = '0', until the next reload.
  on(MarketResearchActions.rejectMatchSuccess, (state, { queueId }) => {
    const rejected = { matchingStatus: 'NO_MATCH', matchedId: '0' };
    return {
      ...state,
      reviewItems: patchQueueItem(state.reviewItems, queueId, rejected),
      autoMatched: patchQueueItem(state.autoMatched, queueId, rejected),
      allQueueItems: patchQueueItem(state.allQueueItems, queueId, rejected),
      noMatchItems: patchQueueItem(state.noMatchItems, queueId, rejected),
      isLoading: false,
    };
  }),

  on(MarketResearchActions.rejectMatchFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  })),

  on(MarketResearchActions.saveToDatabase, (state, { mode }) => ({
    ...state,
    isLoading: true,
    importMode: mode,
  })),

  // Stay on the Export screen and show the done banner — don't reset the
  // whole flow out from under the user the moment the import finishes.
  on(MarketResearchActions.saveToDatabaseSuccess, (state, { result }) => ({
    ...state,
    isLoading: false,
    importCompleted: true,
    importResult: result,
  })),

  on(MarketResearchActions.saveToDatabaseFail, (state, { error }) => ({
    ...state,
    isLoading: false,
    error,
  }))
);
